import { formatDate, type MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, ButtonLink } from "@wato/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { AssistantAction } from "../../api/owner-assistant-types";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { actionErrorKey, useOwnerAssistant } from "./OwnerAssistantProvider";
import { safeRelativePath } from "./suggested-questions";

const key = (suffix: string) => `dashboard.ownerAssistant.action.${suffix}` as MessageKey;

const WHEN: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
const MESSAGE_CLIP = 280;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const clip = (v: string) => (v.length > MESSAGE_CLIP ? `${v.slice(0, MESSAGE_CLIP)}…` : v);
/** Short, display-only id (first 8 chars of a UUID). */
const shortId = (v: string) => v.slice(0, 8);

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="m-0 text-sm">{children}</dd>
    </div>
  );
}

/** OWN-163: a proposed action. NEVER runs by itself — only the explicit «Confirm» click calls the API. */
export function AssistantActionCard({ action }: { action: AssistantAction }) {
  const { t, locale } = useI18n();
  const { workspace } = useWorkspace();
  const { actionStates, runAction, cancelAction } = useOwnerAssistant();
  const state = actionStates[action.id];
  const status = state?.status;
  const doneHref = state?.status === "done" && state.href ? safeRelativePath(state.href) : null;
  const conversationId = str(action.params.conversationId);
  const conversationHref = UUID.test(conversationId)
    ? safeRelativePath(`/conversations?c=${encodeURIComponent(conversationId)}`)
    : null;
  return (
    <article
      className="flex flex-col gap-2 rounded-2xl border border-line bg-surface p-4"
      data-testid="assistant-action"
      data-status={status ?? "pending"}
    >
      <header className="text-xs font-medium text-fg-muted">{t(key(`kind.${action.kind}`))}</header>
      <p className="m-0 text-sm" dir="auto">
        {action.summary}
      </p>
      <dl className="m-0 flex flex-col gap-1.5" data-testid="assistant-action-details">
        {action.kind === "create_task" ? (
          <>
            {str(action.params.title) ? (
              <Detail label={t(key("detail.title"))}>
                <span dir="auto">{str(action.params.title)}</span>
              </Detail>
            ) : null}
            {str(action.params.dueAt) ? (
              <Detail label={t(key("detail.due"))}>
                {formatDate(locale, str(action.params.dueAt), WHEN)}
              </Detail>
            ) : null}
          </>
        ) : null}
        {action.kind === "draft_broadcast" ? (
          <>
            {str(action.params.name) ? (
              <Detail label={t(key("detail.name"))}>
                <span dir="auto">{str(action.params.name)}</span>
              </Detail>
            ) : null}
            {str(action.params.text) ? (
              <Detail label={t(key("detail.message"))}>
                <span dir="auto" className="whitespace-pre-wrap">
                  {clip(str(action.params.text))}
                </span>
              </Detail>
            ) : null}
          </>
        ) : null}
        {action.kind === "assign_conversation" ? (
          <>
            {conversationId ? (
              <Detail label={t(key("detail.conversation"))}>
                <span className="flex flex-wrap items-center gap-2">
                  <bdi dir="ltr" className="font-mono text-xs">
                    {shortId(conversationId)}
                  </bdi>
                  {conversationHref ? (
                    <Link className="underline" to={`/w/${workspace.id}${conversationHref}`}>
                      {t(key("detail.openConversation"))}
                    </Link>
                  ) : null}
                </span>
              </Detail>
            ) : null}
            {str(action.params.userId) ? (
              <Detail label={t(key("detail.assignee"))}>
                <bdi dir="ltr" className="font-mono text-xs">
                  {str(action.params.userId)}
                </bdi>
              </Detail>
            ) : null}
          </>
        ) : null}
      </dl>
      {action.kind === "draft_broadcast" ? (
        <p className="m-0 text-xs text-fg-muted">{t(key("draftNote"))}</p>
      ) : null}
      {status === undefined || status === "running" || status === "error" ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-fg-muted">{t(key("confirmTitle"))}</span>
          <div className="flex gap-2">
            <Button size="sm" loading={status === "running"} onClick={() => void runAction(action)}>
              {t(key("confirm"))}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={status === "running"}
              onClick={() => cancelAction(action.id)}
            >
              {t(key("cancel"))}
            </Button>
          </div>
        </div>
      ) : null}
      {status === "done" ? (
        <div className="flex flex-wrap items-center gap-2" role="status">
          <span className="text-sm">{t(key("done"))}</span>
          {doneHref ? (
            <ButtonLink
              as={Link}
              size="sm"
              variant="secondary"
              to={`/w/${workspace.id}${doneHref}`}
            >
              {t(key("openPage"))}
            </ButtonLink>
          ) : null}
        </div>
      ) : null}
      {status === "cancelled" ? (
        <p className="m-0 text-sm text-fg-muted" role="status">
          {t(key("cancelled"))}
        </p>
      ) : null}
      {state?.status === "error" ? (
        <p role="alert" className="m-0 text-sm text-danger-fg">
          {t(actionErrorKey(state.code))}
        </p>
      ) : null}
    </article>
  );
}
